// src/components/Profile/SecuritySection.js
// Section sécurité (changer MDP, désactiver/supprimer compte)

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Lock, AlertTriangle, Trash2, Eye, EyeOff } from 'lucide-react';
import profileService from '../../services/profileService';
import './SecuritySection.css';

const SecuritySection = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  // États des modals
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // États formulaires
  const [codeForm, setCodeForm] = useState({
    currentCode: '',
    newCode: '',
    confirmCode: ''
  });
  const [deactivateCode, setDeactivateCode] = useState('');
  const [deleteForm, setDeleteForm] = useState({
    secretCode: '',
    confirmation: ''
  });

  // États UI
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [accountStats, setAccountStats] = useState(null);

  // Changer mot de passe
  const handleChangeCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (!/^\d{4,6}$/.test(codeForm.newCode)) {
      setMessage({ type: 'error', text: 'Le code secret doit contenir 4 à 6 chiffres' });
      setLoading(false);
      return;
    }

    if (codeForm.newCode !== codeForm.confirmCode) {
      setMessage({ type: 'error', text: 'Les codes ne correspondent pas' });
      setLoading(false);
      return;
    }

    const result = await profileService.changePassword(
      codeForm.currentCode,
      codeForm.newCode
    );

    if (result.success) {
      setMessage({ type: 'success', text: 'Code secret modifié avec succès' });
      setTimeout(() => {
        setShowPasswordModal(false);
        setCodeForm({ currentCode: '', newCode: '', confirmCode: '' });
        setMessage({ type: '', text: '' });
      }, 2000);
    } else {
      setMessage({ type: 'error', text: result.message });
    }

    setLoading(false);
  };

  // Désactiver compte
  const handleDeactivate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    const result = await profileService.deactivateAccount(deactivateCode);

    if (result.success) {
      setMessage({ type: 'success', text: 'Compte désactivé avec succès' });
      setTimeout(() => {
        logout();
        navigate('/');
      }, 2000);
    } else {
      setMessage({ type: 'error', text: result.message });
    }

    setLoading(false);
  };

  // Ouvrir modal suppression
  const handleOpenDeleteModal = async () => {
    setShowDeleteModal(true);
    // Charger les stats
    const result = await profileService.getAccountStats();
    if (result.success) {
      setAccountStats(result.stats);
    }
  };

  // Supprimer compte
  const handleDelete = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validation
    if (deleteForm.confirmation !== 'SUPPRIMER') {
      setMessage({ type: 'error', text: 'Veuillez taper "SUPPRIMER" pour confirmer' });
      setLoading(false);
      return;
    }

    const result = await profileService.deleteAccount(
      deleteForm.secretCode,
      deleteForm.confirmation
    );

    if (result.success) {
      setMessage({ type: 'success', text: 'Compte supprimé avec succès' });
      setTimeout(() => {
        logout();
        navigate('/');
      }, 2000);
    } else {
      setMessage({ type: 'error', text: result.message });
    }

    setLoading(false);
  };

  return (
    <div className="security-section">
      <h2>Sécurité</h2>
      <p className="section-subtitle">
        Gérez la sécurité de votre compte
      </p>

      {/* Actions */}
      <div className="security-actions">
        
        {/* Changer mot de passe */}
        <div className="security-card">
          <div className="card-icon password">
            <Lock size={24} />
          </div>
          <div className="card-content">
            <h3>Changer le code secret</h3>
            <p>Modifiez votre code secret régulièrement pour plus de sécurité</p>
          </div>
          <button 
            className="btn-secondary"
            onClick={() => setShowPasswordModal(true)}
          >
            Modifier
          </button>
        </div>

        {/* Désactiver compte */}
        <div className="security-card">
          <div className="card-icon warning">
            <AlertTriangle size={24} />
          </div>
          <div className="card-content">
            <h3>Désactiver mon compte</h3>
            <p>Désactivation temporaire, vous pourrez le réactiver en vous reconnectant</p>
          </div>
          <button 
            className="btn-warning"
            onClick={() => setShowDeactivateModal(true)}
          >
            Désactiver
          </button>
        </div>

        {/* Supprimer compte */}
        <div className="security-card">
          <div className="card-icon danger">
            <Trash2 size={24} />
          </div>
          <div className="card-content">
            <h3>Supprimer définitivement</h3>
            <p>Action irréversible, toutes vos données seront supprimées</p>
          </div>
          <button 
            className="btn-danger"
            onClick={handleOpenDeleteModal}
          >
            Supprimer
          </button>
        </div>

      </div>

      {/* ========================================
          MODAL CHANGER MOT DE PASSE
          ======================================== */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Changer le code secret</h3>
              <button 
                className="close-btn"
                onClick={() => setShowPasswordModal(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleChangeCode} className="modal-form">
              
              <div className="form-group">
                <label>Code secret actuel</label>
                <div className="password-input">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    inputMode="numeric"
                    maxLength={6}
                    value={codeForm.currentCode}
                    onChange={(e) => setCodeForm({
                      ...codeForm,
                      currentCode: e.target.value.replace(/\D/g, '').slice(0, 6)
                    })}
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Nouveau code secret</label>
                <div className="password-input">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    inputMode="numeric"
                    maxLength={6}
                    value={codeForm.newCode}
                    onChange={(e) => setCodeForm({
                      ...codeForm,
                      newCode: e.target.value.replace(/\D/g, '').slice(0, 6)
                    })}
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="field-note">4 à 6 chiffres</p>
              </div>

              <div className="form-group">
                <label>Confirmer le nouveau code</label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={codeForm.confirmCode}
                  onChange={(e) => setCodeForm({
                    ...codeForm,
                    confirmCode: e.target.value.replace(/\D/g, '').slice(0, 6)
                  })}
                  required
                />
              </div>

              {/* Message */}
              {message.text && (
                <div className={`form-message ${message.type}`}>
                  {message.type === 'success' ? '✅' : '⚠️'} {message.text}
                </div>
              )}

              {/* Actions */}
              <div className="modal-actions">
                <button 
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={loading}
                >
                  Annuler
                </button>
                <button 
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Modification...' : 'Confirmer'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* ========================================
          MODAL DÉSACTIVER COMPTE
          ======================================== */}
      {showDeactivateModal && (
        <div className="modal-overlay" onClick={() => setShowDeactivateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Désactiver le compte</h3>
              <button 
                className="close-btn"
                onClick={() => setShowDeactivateModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="warning-box">
                <AlertTriangle size={48} color="#F59E0B" />
                <p>
                  Votre compte sera temporairement désactivé. Vous pourrez 
                  le réactiver à tout moment en vous reconnectant.
                </p>
              </div>

              <form onSubmit={handleDeactivate} className="modal-form">
                
                <div className="form-group">
                  <label>Confirmez avec votre code secret</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    value={deactivateCode}
                    onChange={(e) => setDeactivateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Votre code secret"
                    required
                  />
                </div>

                {message.text && (
                  <div className={`form-message ${message.type}`}>
                    {message.type === 'success' ? '✅' : '⚠️'} {message.text}
                  </div>
                )}

                <div className="modal-actions">
                  <button 
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowDeactivateModal(false)}
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button 
                    type="submit"
                    className="btn-warning"
                    disabled={loading}
                  >
                    {loading ? 'Désactivation...' : 'Désactiver'}
                  </button>
                </div>

              </form>
            </div>
          </div>
        </div>
      )}

      {/* ========================================
          MODAL SUPPRIMER COMPTE
          ======================================== */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Supprimer définitivement</h3>
              <button 
                className="close-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="danger-box">
                <Trash2 size={48} color="#EF4444" />
                <p>
                  <strong>Action irréversible !</strong><br />
                  Toutes vos données seront définitivement supprimées.
                </p>
              </div>

              {/* Stats */}
              {accountStats && (
                <div className="stats-box">
                  <h4>Vous allez perdre :</h4>
                  <ul>
                    {accountStats.totalReservations > 0 && (
                      <li>📅 {accountStats.totalReservations} réservation{accountStats.totalReservations > 1 ? 's' : ''}</li>
                    )}
                    {accountStats.totalReviews > 0 && (
                      <li>⭐ {accountStats.totalReviews} avis</li>
                    )}
                    {accountStats.totalConversations > 0 && (
                      <li>💬 {accountStats.totalConversations} conversation{accountStats.totalConversations > 1 ? 's' : ''}</li>
                    )}
                  </ul>
                </div>
              )}

              <form onSubmit={handleDelete} className="modal-form">
                
                <div className="form-group">
                  <label>Tapez "SUPPRIMER" pour confirmer</label>
                  <input
                    type="text"
                    value={deleteForm.confirmation}
                    onChange={(e) => setDeleteForm({
                      ...deleteForm,
                      confirmation: e.target.value
                    })}
                    placeholder="SUPPRIMER"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Votre code secret</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    value={deleteForm.secretCode}
                    onChange={(e) => setDeleteForm({
                      ...deleteForm,
                      secretCode: e.target.value.replace(/\D/g, '').slice(0, 6)
                    })}
                    required
                  />
                </div>

                {message.text && (
                  <div className={`form-message ${message.type}`}>
                    {message.type === 'success' ? '✅' : '⚠️'} {message.text}
                  </div>
                )}

                <div className="modal-actions">
                  <button 
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button 
                    type="submit"
                    className="btn-danger"
                    disabled={loading}
                  >
                    {loading ? 'Suppression...' : 'Supprimer définitivement'}
                  </button>
                </div>

              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SecuritySection;